import api from '@/factories/api';

import map from 'lodash/map';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import { asyncForEach } from '@/lib/misc';
import { onParent } from '@/lib/parentConnection';

export default {
  async loadCourses({ commit, state }, { schemaLevel }) {
    if (state.loadingCourses !== null) {
      return false;
    }

    commit('setLoadingCourses', true);

    try {
      const courses = await api().get(`cg/campaigns?schema=${schemaLevel}`);
      const storeCoursesClone = map(state.courses, clone);
      commit('setCourses', {
        courses: mergeArrays(storeCoursesClone, courses),
        schemaLevel,
      });
    } catch (error) {
      throw error;
    } finally {
      commit('setLoadingCourses', false);
    }
  },

  async loadSingleCourse(
    { dispatch, commit, state },
    { id, schemaLevel, forceReload, background }
  ) {
    const course = state.courses.find((i) => i.id === id);
    if (course && course.schemaLevel === 'full' && !forceReload) {
      return false;
    }

    if (!background) {
      commit('setLoadingSingleCourse', true);
    }

    try {
      schemaLevel = schemaLevel || 'full';
      let path = `cg/campaigns/${id}?schema=${schemaLevel}`;

      let course = await api().get(path);
      course = course[0] || {};
      course.schemaLevel = schemaLevel;

      dispatch('updateCourses', course);
    } catch (error) {
      throw error;
    } finally {
      if (!background) {
        commit('setLoadingSingleCourse', false);
      }
    }
  },

  async syncCourseUpdates({ dispatch, commit, state }, { triggers }) {
    await asyncForEach(triggers, (trigger) => {
      //TODO, check to make sure this runs synchronously...
      if (trigger.type === 'join') {
        return dispatch('loadSingleCourse', {
          id: trigger.join_campaign_id,
          forceReload: true,
          background: true,
        });
      }

      //don't really know what to do here.
      let course = state.courses.find((c) => c.id === trigger.campaign_id);
      if (!course) {
        return false;
      }

      course = cloneDeep(course);

      //fix indexing?
      let courseTriggers = course.triggers || [];

      let existingTrigger = courseTriggers.find((t) => t.id === trigger.id);
      if (!existingTrigger) {
        courseTriggers.push(trigger);
      }

      course.triggers = courseTriggers;

      dispatch('updateCourses', course);
    });
  },

  async joinCourse({ dispatch, commit }, data) {
    commit('setLoadingSingleCourse', true);
    try {
      const response = await api().post('cg/join', data);

      // Only reload course if join is final (no further billing action needed)
      if (!response?.billing_action) {
        dispatch('loadSingleCourse', {
          id: data.campaign_id,
          forceReload: true,
        });
      }

      return response;
    } catch (error) {
      throw error;
    } finally {
      commit('setLoadingSingleCourse', false);
    }
  },

  finishCourseLesson({ dispatch, state }, item) {
    const course = state.courses.find((i) => i.id === item.campaign_id);
    const lesson = (course?.triggers || []).find((i) => i.id === item.id);

    if (!lesson || !course) return;

    if (!lesson.finished) {
      dispatch('updateCourses', {
        ...course,
        triggers: course.triggers.map((i) =>
          i.id === lesson.id ? { ...i, finished: true } : i
        ),
      });
    }

    dispatch('updateScormCourseState');
  },

  updateScormCourseState({ rootGetters }) {
    if (!rootGetters.insideScorm) return false;

    onParent('setCourseProgress', (action) =>
      action({
        last_seen: new Date().getTime(),
      })
    );
  },

  updateCourses({ commit, state }, course) {
    const courses = map(state.courses, clone);
    const courseIndex = courses.findIndex((i) => i.id == course.id);
    if (courseIndex === -1) {
      courses.push(course);
    } else {
      courses[courseIndex] = course;
    }
    commit('setCourses', { courses });
  },

  async hydrateWorkflowCourses({ dispatch, state }) {
    const ids = state.courses
      .filter(
        (c) =>
          c.type === 'workflow' &&
          c.status === 'playing' &&
          c.schemaLevel !== 'full'
      )
      .map((c) => c.id);

    if (!ids.length) return;

    await Promise.all(
      ids.map((id) =>
        dispatch('loadSingleCourse', { id, background: true }).catch(() => {})
      )
    );
  },

  async loadBillingInfo({ dispatch, commit }, course) {
    commit('setLoadingSingleCourse', true);
    try {
      const billingInfo = await api().get(
        `cg/billing?campaign_id=${course.id}`
      );
      commit('setCourseSubscription', {
        campaign_id: course.id,
        ...billingInfo,
      });
      const courseCopy = Object.assign(cloneDeep(course), billingInfo);
      dispatch('updateCourses', courseCopy);
    } catch (error) {
      throw error;
    } finally {
      commit('setLoadingSingleCourse', false);
    }
  },

  async cancelSubscription({}, campaignId) {
    return await api().post('cg/cancel_subscription', {
      campaign_id: campaignId,
    });
  },

  async loadCourseSubscription({ commit, getters }, { courseId, refresh }) {
    if (getters.getCourseSubscription(courseId) && !refresh) return;

    try {
      commit('setLoadingCourseSubscription', courseId);
      const subscription = await api().get(
        `cg/billing?campaign_id=${courseId}`
      );
      commit('setCourseSubscription', {
        campaign_id: courseId,
        ...subscription,
      });
    } finally {
      commit('setLoadingCourseSubscription', null);
    }
  },

  async subscribeToCourse({}, params) {
    return await api().post('cg/join', params);
  },

  async updatePortalBilling({}, params) {
    return await api().put('cg/billing', params);
  },
};

const mergeArrays = (rootArr, newArr) => {
  return newArr.reduce(
    (acc, course) => {
      if (rootArr.findIndex((i) => i.id === course.id) === -1) {
        acc.push(course);
      }
      return acc;
    },
    [...rootArr]
  );
};
